import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import { isFirefox, isWindows } from 'react-device-detect';
import styled from 'styled-components';
import { useHasSupportedVideoCodec } from '../../hooks/useHasSupportedVideoCodec';
import { useTrackBroadcastDurationEffect } from '../../hooks/useTrackBroadcastDurationEffect';
import { useWakeLock } from '../../hooks/useWakeLock';
import { BroadcastStateService } from '../../services/broadcaststate/BroadcastStateService';
import { ViewerService } from '../../services/viewer/ViewerService';
import { useTimedFlag } from '../../utils/time';
import { Canvas } from '../Canvas';
import { FullscreenButton } from '../FullscreenButton';
import { ViewerViewHud } from '../huds/ViewerViewHud';
import { LoadingIndicator } from '../LoadingIndicator';
import { CodecNotInstalled, CodecNotSupported, FirefoxNotSupported, } from '../NotSupportedMessage';
import { SidebarSlot } from '../SidebarSlot';
import { Stage, StageContainer } from '../Stage';
import { SplitShell } from '../shells/SplitShell';
import { StackShell } from '../shells/StackShell';
import { VideoDisplay } from '../VideoDisplay';
import { ViewerPreLive } from '../ViewerPreLive';
import { ViewerFooter } from '../viewer/ViewerFooter';
import { ViewerHeader } from '../viewer/ViewerHeader';
export function ViewerView() {
    const activeBroadcast = BroadcastStateService.getOutput().useRequiredActiveBroadcast();
    const ready = ViewerService.getOutput().useReady();
    const isVideoStreaming = ViewerService.getOutput().useIsVideoStreaming();
    const isAudioOnMute = ViewerService.getOutput().useAudioMuted();
    const viewerService = ViewerService.getNotificationInterface();
    const [showSidebar, setShowSidebar] = useState(true);
    useTrackBroadcastDurationEffect({
        enabled: isVideoStreaming,
        action: 'Watch',
    });
    const browserVideoCodecSupported = useHasSupportedVideoCodec();
    const stageContainerRef = useRef(null);
    const [isHudVisible, showHud] = useTimedFlag(true, 5000);
    useWakeLock({ enabled: true });
    const handleCanvasMount = useCallback((canvas) => {
        if (canvas) {
            viewerService.notifyCanvasMounted(canvas);
        }
    }, [viewerService]);
    if (isFirefox) {
        return _jsx(FirefoxNotSupported, {});
    }
    if (browserVideoCodecSupported === false) {
        if (isWindows) {
            return _jsx(CodecNotInstalled, {});
        }
        return _jsx(CodecNotSupported, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: !ready }), _jsx(ViewerViewRoot, { onTouchStart: () => {
                    showHud();
                }, onMouseDown: () => {
                    showHud();
                }, onMouseOver: () => {
                    showHud();
                }, before: _jsx(ViewerHeader, {}), after: _jsx(ViewerFooter, { audioMuted: isAudioOnMute, onSidebarClick: () => {
                        setShowSidebar((c) => !c);
                    }, showSidebar: showSidebar }), children: _jsx(SplitShell, { isSidebarOpen: showSidebar, sidebar: _jsx(SidebarSlot, {}), children: _jsx(StageContainer, { ref: stageContainerRef, children: _jsxs(Stage, { children: [_jsx(VideoDisplay, { fit: "contain", component: _jsx(Canvas, { onCanvasMount: handleCanvasMount }), hud: _jsx(ViewerViewHud, { visible: isHudVisible, fullscreenButton: _jsx(FullscreenButton, { element: stageContainerRef.current }) }) }), !isVideoStreaming && (_jsx(ViewerPreLive, { broadcastStreamId: activeBroadcast.streamId, broadcasterUserId: activeBroadcast.broadcasterUserId }))] }) }) }) })] }));
}
const ViewerViewRoot = styled(StackShell) `
    --tv-ui-VideoContainer-video-border-radius: var(--tv-ui-border-radius-1);
    height: 100vh;

    @media (min-width: 60rem) {
        --SplitShell_SidebarWidth: 25.25rem;
    }
`;
